import React, {FC, useMemo, useState} from "react";
import {
    Box,
    Button,
    ChakraProps,
    IconButton,
    Collapse,
    Divider,
    Flex,
    Grid,
    GridItem,
    HStack,
    Icon,
    Input,
    Menu,
    MenuButton,
    MenuDivider,
    MenuGroup,
    MenuItem,
    MenuList,
    Radio,
    RadioGroup,
    Select,
    Stack,
    Tag,
    TagCloseButton,
    TagLabel,
    Text,
    Tooltip,
    VStack,
} from "@chakra-ui/react";
import {format} from "date-fns";
import enUS from "date-fns/locale/en-US";
import {DatePickerUpdateStyled} from "../../components/DatePicker/DatePicker";
import {BoxRowStyleConfig} from "../Forms/FormStyleConfigs";
import {
    FiChevronDown,
    FiChevronUp,
    FiDownload,
    FiEye,
    FiFilter,
    FiPrinter,
    FiRepeat,
    FiSend,
    FiShoppingBag,
    FiX,
} from "react-icons/all";
import {FiCreditCard} from "react-icons/fi";
import {BsThreeDotsVertical} from "react-icons/bs";
import {AiOutlineFileExcel} from "react-icons/ai";
import {AngleDoubleRight} from "../../components/Inputs/Icons/Icons";
import {generalColor} from "../../theme/foundations/breakpoints";
import {hasPermission} from "../../features/Permission/AllowedAccess";
import {dealershipExcludeSelectPermission} from "../permissions";

/** Минимум полей заказа для строки выбранных id */
export type OrderSelectionItem = {id: number};

export type StatusFilterOption = {id: number; name: string};

/** Меню Action: флаги и колбэки без привязки к OrderHistory */
export interface OrderHistoryActionMenuConfig {
    hasExportSection: boolean;
    hasBulkInvoiceSection: boolean;
    hasNextStageSection: boolean;
    showInvoiceMenuItem: boolean;
    canPrintSelect: boolean;
    canSendSelect: boolean;
    sendSelectDisabled: boolean;
    nextStageDisabled: boolean;
    onExportStatement: () => void;
    onExportSales: () => void;
    onDownloadInvoice: () => void;
    onShowInvoice: () => void;
    onPrintSelect: () => void;
    onSendSelect: () => void;
    onBulkToPaid: () => void;
}

export interface OrderHistorySelectionToolbarProps {
    showFilterPanel?: boolean;
    isFiltersOpen: boolean;
    onFiltersOpenToggle: () => void;
    getFilterValue: (key: string) => string;
    setColumnFilterValue: (columnId: string, value: string) => void;
    resetAllFilters: () => void;
    dateFromPicker: Date | null;
    dateToPicker: Date | null;
    setDateFromPicker: (date: Date | null) => void;
    setDateToPicker: (date: Date | null) => void;
    dealershipSuggestions: string[];
    showDealershipColumn: boolean;
    showAmountColumn: boolean;
    showStatusColumn: boolean;
    statusFilterOptions: StatusFilterOption[];
    currentSortLabel: string;
    onResetSort: () => void;

    isExcluded: string;
    onExcludedChange: (value: string) => void;
    countSelected: number;
    onClearSelection: () => void;
    selectedItems: OrderSelectionItem[];
    onRemoveSelectedOrder: (item: OrderSelectionItem) => void;
    isMobile: boolean;
    uiModeIsLegacy?: boolean;
    onUiModeToggle?: (isLegacy: boolean) => void;
    /** Меню Action; если не передано — кнопка не показывается */
    actionMenu?: OrderHistoryActionMenuConfig;
    /** Только админ: фильтр по Card paid + бейдж с количеством */
    showCardPaidStatusFilter?: boolean;
    cardPaidOrderCount?: number | null;
    isCardPaidFilterActive?: boolean;
    onCardPaidFilterClick?: () => void;
}

const filterControlProps = {
    h: "32px",
    minH: "32px",
    size: "sm" as const,
    borderRadius: "md",
    fontSize: "xs",
    lineHeight: "1.25rem",
    // px: 2,
    borderColor: "gray.200",
    bg: "white",
    _hover: {borderColor: "gray.300"},
    _focusVisible: {
        borderColor: "blue.400",
        boxShadow: "0 0 0 1px var(--chakra-colors-blue-400)",
    },
};

/** Как у остальных полей фильтра (`filterControlProps`): белый фон, без серой «кнопки» внутри */
const orderHistoryDateFilterWrapperProps: ChakraProps = {
    h: "32px",
    minH: "32px",
    flex: "1 1 0%",
    minW: 0,
    maxW: "100%",
    w: "100%",
    display: "flex",
    alignItems: "center",
    px: 2,
    py: 0,
    fontSize: "xs",
    lineHeight: "1.25rem",
    color: "gray.800",
    bg: "white",
    borderColor: "gray.200",
    borderWidth: "1px",
    borderRadius: "md",
    _hover: {
        borderColor: "gray.300",
    },
    _focusWithin: {
        borderColor: "blue.400",
        boxShadow: "0 0 0 1px var(--chakra-colors-blue-400)",
    },
    sx: {
        "& .react-datepicker__input-container > input": {
            fontSize: "12px",
        },
        "& .react-datepicker__input-container > input::placeholder": {
            fontSize: "12px",
        },
    },
};

/** Толстый крестик для обычных (не date) фильтров */
const filterClearIcon = <Icon as={FiX} boxSize={4} strokeWidth={5} color="gray.300" _hover={{ color: "gray.500"}}/>;

const filterClearButtonProps = {
    size: "xs" as const,
    variant: "ghost" as const,
    position: "absolute" as const,
    right: "10px",
    top: "50%",
    transform: "translateY(-50%)",
    zIndex: 1,
    w: "12px",
    h: "12px",
    minH: "12px",
    minW: "12px",
    p: 0,
    borderRadius: "md",
    color: "gray.600",
    lineHeight: 1,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    bg: "transparent",
    _hover: {bg: "transparent", color: "gray.700"},
    _active: {bg: "transparent"},
};

/** Общая «поверхность» как у кнопки Cart (фон, шрифт, скругление) */
const navbarCartLikeSurfaceProps = {
    fontFamily: "Poppins",
    fontSize: "12px",
    fontWeight: "400",
    borderRadius: "5px",
    bg: generalColor.primaryGrayLight,
    color: "gray.800",
};

/** Как у `CartButton` в навбаре: светлый фон, скругление 6px, подчёркивание при hover */
const navbarCartLikeButtonProps = {
    ...navbarCartLikeSurfaceProps,
    minW: "auto",
    _hover: {
        bg: "transparent",
        textDecoration: "underline",
    },
};

const shouldShowOrderIdTags = (selectedItems: OrderSelectionItem[], isExcluded: string) =>
    (selectedItems && selectedItems.length > 1) || isExcluded === "2";

function buildActiveFiltersSummary(
    getFilterValue: (key: string) => string,
    statusFilterOptions: StatusFilterOption[],
    showDealershipColumn: boolean,
    showAmountColumn: boolean,
    showStatusColumn: boolean,
): string {
    const parts: string[] = [];
    const from = getFilterValue("dateFrom");
    if (from) parts.push(`From ${from}`);
    const to = getFilterValue("dateTo");
    if (to) parts.push(`To ${to}`);
    const order = getFilterValue("order");
    if (order) parts.push(`Order ${order}`);
    const user = getFilterValue("user");
    if (user) parts.push(`User ${user}`);
    if (showDealershipColumn) {
        const d = getFilterValue("dealership");
        if (d) parts.push(`Dealership ${d}`);
    }
    if (showAmountColumn) {
        const a = getFilterValue("amount");
        if (a) parts.push(`Amount ${a}`);
    }
    if (showStatusColumn) {
        const s = getFilterValue("status");
        if (s) {
            const opt = statusFilterOptions.find((o) => o.id >= 0 && String(o.id) === s);
            const label = opt?.name?.trim() ? opt.name : s;
            parts.push(`Status: ${label}`);
        }
    }
    return parts.length > 0 ? parts.join(" · ") : "None";
}

const actionMenuListSx = {".chakra-menu__menuitem": {py: 0.5, minH: "11px", fontSize: "12px"}};

const actionMenuFiIcon = (C: typeof FiDownload) => <Icon as={C} fontSize="18px" strokeWidth={1}/>;

function CardPaidFilterShortcut(props: {
    count: number | null;
    isActive: boolean;
    onClick: () => void;
}) {
    const {count, isActive, onClick} = props;
    return (
        <Box position="relative" display="inline-flex" alignItems="center" justifyContent="center">
            <Tooltip label={isActive ? "Clear Card paid filter" : "Filter: Card paid"}>
                <IconButton
                    aria-label="Filter orders with Card paid status"
                    size="xs"
                    icon={<Icon as={FiCreditCard} boxSize={4}/>}
                    variant={isActive ? "solid" : "outline"}
                    colorScheme={isActive ? "purple" : "gray"}
                    onClick={onClick}
                />
            </Tooltip>
            {count !== null && count > 0 && (
                <Flex
                    position="absolute"
                    top="-5px"
                    right="-6px"
                    minW="16px"
                    h="16px"
                    px={count > 9 ? "2px" : 0}
                    borderRadius="full"
                    bg="red.500"
                    color="white"
                    fontSize="10px"
                    fontWeight="700"
                    align="center"
                    justify="center"
                    lineHeight="1"
                    pointerEvents="none"
                >
                    {count > 99 ? "99+" : count}
                </Flex>
            )}
        </Box>
    );
}

/**
 * Карточка: фильтры (сворачиваемый блок), сортировка, Include/Exclude и теги выбранных заказов.
 */
export const OrderHistorySelectionToolbar: FC<OrderHistorySelectionToolbarProps> = ({
    showFilterPanel = true,
    isFiltersOpen,
    onFiltersOpenToggle,
    getFilterValue,
    setColumnFilterValue,
    resetAllFilters,
    dateFromPicker,
    dateToPicker,
    setDateFromPicker,
    setDateToPicker,
    dealershipSuggestions,
    showDealershipColumn,
    showAmountColumn,
    showStatusColumn,
    statusFilterOptions,
    currentSortLabel,
    onResetSort,
    isExcluded,
    onExcludedChange,
    countSelected,
    onClearSelection,
    selectedItems,
    onRemoveSelectedOrder,
    isMobile,
    uiModeIsLegacy,
    onUiModeToggle,
    actionMenu,
    showCardPaidStatusFilter,
    cardPaidOrderCount,
    isCardPaidFilterActive,
    onCardPaidFilterClick,
}) => {
    /** Только новый UI (`modern`): выпадающее меню «Action». Старый UI — ряд иконок. При пропе не передан — считаем legacy. */
    const useActionMenuDropdown = uiModeIsLegacy === false;
    const isExcludeMode = isExcluded === "2";
    const showOrderIdTagRow = shouldShowOrderIdTags(selectedItems, isExcluded);
    const highlightActionForSelection = selectedItems.length > 0;
    const [isDealershipDropdownOpen, setIsDealershipDropdownOpen] = useState(false);
    const dealershipFilterValue = getFilterValue("dealership");
    const filteredDealershipSuggestions = useMemo(() => {
        const query = dealershipFilterValue.trim().toLowerCase();
        const source = dealershipSuggestions.filter(Boolean);
        if (!query) return source.slice(0, 100);
        return source.filter((name) => name.toLowerCase().includes(query)).slice(0, 100);
    }, [dealershipSuggestions, dealershipFilterValue]);
    const activeFiltersSummary = buildActiveFiltersSummary(
        getFilterValue,
        statusFilterOptions,
        showDealershipColumn,
        showAmountColumn,
        showStatusColumn,
    );
    const hasActiveFilters = activeFiltersSummary !== "None";

    return (
        <Box
            borderRadius="lg"
            bg="white"
            boxShadow="0 1px 2px rgba(0, 0, 0, 0.04)"
            overflow="visible"
            position="relative"
            _before={{
                content: '""',
                position: "absolute",
                inset: 0,
                borderRadius: "inherit",
                border: uiModeIsLegacy ? "none" : "1px solid",
                borderColor: uiModeIsLegacy ? "transparent" : "gray.200",
                pointerEvents: "none",
                zIndex: 2,
            }}
        >
            {showFilterPanel && (
                <>
                    <Flex
                        px={{base: 3, md: 4}}
                        py={2}
                        align="center"
                        justify="flex-start"
                        gap={2}
                        flexWrap="wrap"
                        bg="white"
                        borderBottomWidth="1px"
                        borderColor="gray.100"
                    >
                        <HStack spacing={2} align="center">
                            <Flex
                                align="center"
                                justify="center"
                                w="30px"
                                h="30px"
                                borderRadius="md"
                                bg="white"
                                borderWidth="1px"
                                borderColor="gray.200"
                                boxShadow="sm"
                                flexShrink={0}
                            >
                                <Icon as={FiFilter} color={hasActiveFilters ? "orange.400" : "blue.500"} boxSize={4}/>
                            </Flex>
                            <Button
                                {...navbarCartLikeButtonProps}
                                size="xs"
                                h="auto"
                                py={1}
                                px={2}
                                minH={0}
                                leftIcon={<Icon as={isFiltersOpen ? FiChevronUp : FiChevronDown} boxSize={3.5}/>}
                                onClick={onFiltersOpenToggle}
                            >
                                {isFiltersOpen ? "Hide filters" : "Show filters"}
                            </Button>
                            {!isFiltersOpen && hasActiveFilters && (
                                <Flex
                                    alignItems={{base: "flex-start", sm: "center"}}
                                    flexWrap="wrap"
                                    gap={2}
                                >
                                    <Text fontSize="xs" color="gray.600" fontWeight="500" wordBreak="break-word">
                                        <Text as="span" fontWeight="600" color="gray.700">
                                            Active filters:{" "}
                                        </Text>
                                        <Text as="span">{activeFiltersSummary}</Text>
                                    </Text>
                                    <Button
                                        {...navbarCartLikeButtonProps}
                                        size="xs"
                                        h="auto"
                                        py={1}
                                        px={2}
                                        minH={0}
                                        flexShrink={0}
                                        onClick={resetAllFilters}
                                    >
                                        Reset filters
                                    </Button>
                                </Flex>
                            )}
                        </HStack>
                    </Flex>
                    <Collapse in={isFiltersOpen} animateOpacity style={{overflow: "visible"}}>
                        <Box
                            px={{base: 3, md: 4}}
                            py={3}
                            bg="white"
                            borderBottomWidth="1px"
                            borderColor="gray.100"
                            overflow="visible"
                        >
                            <Box overflow="visible">
                        <Grid
                            templateColumns={{
                                base: "repeat(1, minmax(0, 1fr))",
                                md: "repeat(2, minmax(0, 1fr))",
                                lg: "repeat(3, minmax(0, 1fr))",
                                xl: "repeat(4, minmax(0, 1fr))",
                            }}
                            gap={2}
                            w="100%"
                            overflow="visible"
                        >
                            <GridItem
                                colSpan={{base: 1, md: 1, lg: 1, xl: 1}}
                                minW={0}
                            >
                                <Box {...BoxRowStyleConfig} h="auto" w="100%" minW={0} alignItems="center">
                                    <Flex
                                        w="100%"
                                        align="center"
                                        gap={2}
                                        minW={0}
                                        flexWrap="nowrap"
                                    >
                                        <DatePickerUpdateStyled
                                            wrapperProps={orderHistoryDateFilterWrapperProps}
                                            placeholderText="From"
                                            isClearable
                                            selected={dateFromPicker}
                                            locale={enUS}
                                            dateFormat="MM-dd-yyyy"
                                            onChange={(date: Date | null) => {
                                                setDateFromPicker(date);
                                                setColumnFilterValue(
                                                    "dateFrom",
                                                    date ? format(date, "MM-dd-yyyy") : "",
                                                );
                                            }}
                                            onKeyDown={(e) => e.preventDefault()}
                                        />
                                        <Text
                                            as="span"
                                            flexShrink={0}
                                            alignSelf="center"
                                            px={1}
                                            fontSize="md"
                                            color="gray.500"
                                            lineHeight="32px"
                                        >
                                            -
                                        </Text>
                                        <DatePickerUpdateStyled
                                            wrapperProps={orderHistoryDateFilterWrapperProps}
                                            placeholderText="To"
                                            isClearable
                                            selected={dateToPicker}
                                            locale={enUS}
                                            dateFormat="MM-dd-yyyy"
                                            onChange={(date: Date | null) => {
                                                setDateToPicker(date);
                                                setColumnFilterValue(
                                                    "dateTo",
                                                    date ? format(date, "MM-dd-yyyy") : "",
                                                );
                                            }}
                                            onKeyDown={(e) => e.preventDefault()}
                                        />
                                    </Flex>
                                </Box>
                            </GridItem>
                            <Box position="relative" w="100%" minW={0} maxW="100%">
                                <Input
                                    placeholder="Order"
                                    {...filterControlProps}
                                    pr="32px"
                                    value={getFilterValue("order")}
                                    onChange={(e) => setColumnFilterValue("order", e.target.value)}
                                />
                                {getFilterValue("order") && (
                                    <IconButton
                                        aria-label="Clear order filter"
                                        {...filterClearButtonProps}
                                        onClick={() => setColumnFilterValue("order", "")}
                                        icon={filterClearIcon}
                                    />
                                )}
                            </Box>
                            {showDealershipColumn && (
                                <Box position="relative" w="100%" minW={0} maxW="100%">
                                    <Input
                                        placeholder="Dealership"
                                        {...filterControlProps}
                                        pr="32px"
                                        value={dealershipFilterValue}
                                        onFocus={() => setIsDealershipDropdownOpen(true)}
                                        onChange={(e) => {
                                            setColumnFilterValue("dealership", e.target.value);
                                            setIsDealershipDropdownOpen(true);
                                        }}
                                        onBlur={() => {
                                            setTimeout(() => setIsDealershipDropdownOpen(false), 120);
                                        }}
                                    />
                                    {getFilterValue("dealership") && (
                                        <IconButton
                                            aria-label="Clear dealership filter"
                                            {...filterClearButtonProps}
                                            onClick={() => {
                                                setColumnFilterValue("dealership", "");
                                                setIsDealershipDropdownOpen(false);
                                            }}
                                            icon={filterClearIcon}
                                        />
                                    )}
                                    {isDealershipDropdownOpen && filteredDealershipSuggestions.length > 0 && (
                                        <Box
                                            position="absolute"
                                            top="calc(100% + 4px)"
                                            left={0}
                                            right={0}
                                            zIndex={20}
                                            bg="white"
                                            borderWidth="1px"
                                            borderColor="gray.200"
                                            borderRadius="md"
                                            boxShadow="md"
                                            maxH="50vh"
                                            overflowY="auto"
                                        >
                                            {filteredDealershipSuggestions.map((name) => (
                                                <Box
                                                    key={name}
                                                    px={2}
                                                    py={1.5}
                                                    fontSize="xs"
                                                    cursor="pointer"
                                                    _hover={{bg: "gray.50"}}
                                                    onMouseDown={() => {
                                                        setColumnFilterValue("dealership", name);
                                                        setIsDealershipDropdownOpen(false);
                                                    }}
                                                >
                                                    {name}
                                                </Box>
                                            ))}
                                        </Box>
                                    )}
                                </Box>
                            )}
                            <Box position="relative" w="100%" minW={0} maxW="100%">
                                <Input
                                    placeholder="User"
                                    {...filterControlProps}
                                    pr="32px"
                                    value={getFilterValue("user")}
                                    onChange={(e) => setColumnFilterValue("user", e.target.value)}
                                />
                                {getFilterValue("user") && (
                                    <IconButton
                                        aria-label="Clear user filter"
                                        {...filterClearButtonProps}
                                        onClick={() => setColumnFilterValue("user", "")}
                                        icon={filterClearIcon}
                                    />
                                )}
                            </Box>
                            {showAmountColumn && (
                                <Box position="relative" w="100%" minW={0} maxW="100%">
                                    <Input
                                        placeholder="Amount"
                                        {...filterControlProps}
                                        pr="32px"
                                        value={getFilterValue("amount")}
                                        onChange={(e) => setColumnFilterValue("amount", e.target.value)}
                                    />
                                    {getFilterValue("amount") && (
                                        <IconButton
                                            aria-label="Clear amount filter"
                                            {...filterClearButtonProps}
                                            onClick={() => setColumnFilterValue("amount", "")}
                                            icon={filterClearIcon}
                                        />
                                    )}
                                </Box>
                            )}
                            {showStatusColumn && (
                                <Box position="relative" w="100%" minW={0} maxW="100%">
                                    <Select
                                        value={getFilterValue("status")}
                                        {...filterControlProps}
                                        w="100%"
                                        // pl={2}
                                        // pr={getFilterValue("status") ? "4.2rem" : "2.4rem"}
                                        iconSpacing="0.6rem"
                                        whiteSpace="nowrap"
                                        overflow="hidden"
                                        textOverflow="ellipsis"
                                        onChange={(e) => setColumnFilterValue("status", e.target.value)}
                                    >
                                        {statusFilterOptions.map((option) => (
                                            <option style={{fontWeight: "400", fontSize: "14px"}} key={option.id} value={option.id < 0 ? "" : String(option.id)}>
                                                {option.name || "All statuses"}
                                            </option>
                                        ))}
                                    </Select>
                                    {getFilterValue("status") && (
                                        <IconButton
                                            aria-label="Clear status filter"
                                            {...filterClearButtonProps}
                                            right="2.3rem"
                                            onClick={() => setColumnFilterValue("status", "")}
                                            icon={filterClearIcon}
                                        />
                                    )}
                                </Box>
                            )}
                        </Grid>
                        <Flex
                            alignItems={{base: "flex-start", sm: "center"}}
                            flexWrap="wrap"
                            gap={{base: 1.5, sm: 2}}
                            direction={{base: "column", sm: "row"}}
                            mt={3}
                            mb={1}
                        >
                            <Text fontSize="xs" color="gray.600" fontWeight="500">
                                <Text as="span" fontWeight="600" color="gray.700">
                                    Active filters:{" "}
                                </Text>
                                <Text as="span" wordBreak="break-word">
                                    {activeFiltersSummary}
                                </Text>
                            </Text>
                            <Button
                                {...navbarCartLikeButtonProps}
                                size="xs"
                                h="auto"
                                py={1}
                                px={2}
                                minH={0}
                                flexShrink={0}
                                onClick={resetAllFilters}
                            >
                                Reset filters
                            </Button>
                        </Flex>
                        <Divider my={3} borderColor="gray.100"/>
                        <Flex
                            alignItems={{base: "flex-start", sm: "center"}}
                            flexWrap="wrap"
                            gap={{base: 1.5, sm: 2}}
                            direction={{base: "column", sm: "row"}}
                            mb={1}
                        >
                            <Text fontSize="xs" color="gray.600" fontWeight="500">
                                <Text as="span" fontWeight="600" color="gray.700">
                                    Sort:{" "}
                                </Text>
                                <Text as="span" wordBreak="break-word">
                                    {currentSortLabel}
                                </Text>
                            </Text>
                            <Button
                                {...navbarCartLikeButtonProps}
                                size="xs"
                                h="auto"
                                py={1}
                                px={2}
                                minH={0}
                                flexShrink={0}
                                onClick={onResetSort}
                            >
                                Reset sort
                            </Button>
                        </Flex>
                            </Box>
                        </Box>
                    </Collapse>
                </>
            )}

            <Box
                w="100%"
                borderRadius="0"
                bg="white"
                px={{base: 2.5, md: 3.5}}
                py={uiModeIsLegacy ? {base: 1, md: 1} : {base: 2, md: 2.5}}
                mb={0}
            >
                <Flex
                    direction={{base: "column", md: "row"}}
                    alignItems={{base: "stretch", md: "flex-start"}}
                    flexWrap={{base: "nowrap", md: "wrap"}}
                    gap={2}
                    w="100%"
                >
                    <VStack
                        align="stretch"
                        spacing={showOrderIdTagRow ? 2 : 0}
                        flex="1"
                        minW={0}
                        w="100%"
                    >
                        <HStack spacing={2} flexWrap="wrap" alignItems="center" w="100%">
                            <Box
                                bg="white"
                                borderRadius="md"
                                px={{base: 2, md: 2.5}}
                                py={1.5}
                                fontSize="xs"
                                flexShrink={0}
                            >
                                <RadioGroup onChange={onExcludedChange} value={isExcluded}>
                                    <Stack direction="row" spacing={{base: 3, md: 4}}>
                                        <Radio value="1" size="sm" colorScheme="blue">
                                            Include
                                        </Radio>
                                        <Radio value="2" size="sm" colorScheme="red">
                                            Exclude
                                        </Radio>
                                    </Stack>
                                </RadioGroup>
                            </Box>
                            <Tag
                                size="sm"
                                variant="solid"
                                colorScheme={isExcludeMode ? "red" : "blue"}
                                display="inline-flex"
                                w="auto"
                                minW="max-content"
                                maxW="100%"
                                flexShrink={0}
                                alignSelf="center"
                            >
                                <TagLabel
                                    fontSize="xs"
                                    whiteSpace="nowrap"
                                    sx={{fontVariantNumeric: "tabular-nums"}}
                                >
                                    {`Selected ${countSelected}`}
                                </TagLabel>
                                <TagCloseButton aria-label="Clear selection" onClick={onClearSelection}/>
                            </Tag>
                        </HStack>
                        {showOrderIdTagRow && (
                            <Flex
                                direction="row"
                                flexWrap="wrap"
                                alignContent="flex-start"
                                maxHeight={{base: "none", md: "160px"}}
                                overflowY={{base: "visible", md: "auto"}}
                                w="100%"
                                maxW="100%"
                                flex="1 1 100%"
                                alignSelf="stretch"
                                gap={0.5}
                                alignItems="flex-start"
                            >
                                {selectedItems.map((item) => (
                                    <Box px="1px" pb="1px" key={item.id}>
                                        <Tag
                                            size="sm"
                                            variant="solid"
                                            colorScheme={isExcludeMode ? "red" : "blue"}
                                            bg={isExcludeMode ? "red.500" : "blue.500"}
                                            color="white"
                                        >
                                            <TagLabel fontSize="xs">{item.id}</TagLabel>
                                            <TagCloseButton onClick={() => onRemoveSelectedOrder(item)}/>
                                        </Tag>
                                    </Box>
                                ))}
                            </Flex>
                        )}
                    </VStack>
                    {(actionMenu || (typeof uiModeIsLegacy === "boolean" && onUiModeToggle)) && (
                        <Box
                            flexShrink={0}
                            alignSelf="flex-end"
                            ml={{md: "auto"}}
                            w={{base: "100%", md: "auto"}}
                        >
                            <Flex justifyContent={{base: "flex-end", md: "flex-start"}} w="100%">
                            {!useActionMenuDropdown ? (
                                <HStack spacing={1}>
                                    {typeof uiModeIsLegacy === "boolean" && onUiModeToggle && (
                                        <Tooltip label="Switch UI">
                                            <IconButton
                                                aria-label="Switch UI"
                                                size="xs"
                                                mr={1}
                                                icon={<Icon as={FiRepeat}/>}
                                                onClick={() => onUiModeToggle(false)}
                                            />
                                        </Tooltip>
                                    )}
                                    {showCardPaidStatusFilter && onCardPaidFilterClick && (
                                        <Box mr={1}>
                                            <CardPaidFilterShortcut
                                                count={cardPaidOrderCount ?? null}
                                                isActive={!!isCardPaidFilterActive}
                                                onClick={onCardPaidFilterClick}
                                            />
                                        </Box>
                                    )}
                                    {actionMenu?.hasExportSection && (
                                        <Tooltip label="Export Statement">
                                            <IconButton
                                                aria-label="Export statement"
                                                size="xs"
                                                icon={<Icon as={AiOutlineFileExcel}/>}
                                                onClick={actionMenu.onExportStatement}
                                            />
                                        </Tooltip>
                                    )}
                                    {actionMenu?.hasExportSection && (
                                        <Tooltip label="Export of sales">
                                            <IconButton
                                                aria-label="Export sales"
                                                size="xs"
                                                icon={actionMenuFiIcon(FiShoppingBag)}
                                                onClick={actionMenu.onExportSales}
                                            />
                                        </Tooltip>
                                    )}
                                    {actionMenu?.hasBulkInvoiceSection && (
                                        <Tooltip label="Download">
                                            <IconButton aria-label="Download" size="xs" icon={actionMenuFiIcon(FiDownload)}
                                                        onClick={actionMenu.onDownloadInvoice}/>
                                        </Tooltip>
                                    )}
                                    {actionMenu?.hasBulkInvoiceSection && actionMenu.showInvoiceMenuItem && (
                                        <Tooltip label="Show PDF">
                                            <IconButton aria-label="Show PDF" size="xs" icon={actionMenuFiIcon(FiEye)}
                                                        onClick={actionMenu.onShowInvoice}/>
                                        </Tooltip>
                                    )}
                                    {actionMenu?.hasBulkInvoiceSection && actionMenu.canPrintSelect && (
                                        <Tooltip label="Print select">
                                            <IconButton aria-label="Print select" size="xs" icon={actionMenuFiIcon(FiPrinter)}
                                                        onClick={actionMenu.onPrintSelect}/>
                                        </Tooltip>
                                    )}
                                    {actionMenu?.hasBulkInvoiceSection && actionMenu.canSendSelect && (
                                        <Tooltip label="Send select">
                                            <IconButton aria-label="Send select" size="xs" icon={actionMenuFiIcon(FiSend)}
                                                        onClick={actionMenu.onSendSelect}
                                                        isDisabled={actionMenu.sendSelectDisabled}/>
                                        </Tooltip>
                                    )}
                                    {actionMenu?.hasNextStageSection && (
                                        <Tooltip label="To Paid">
                                            <IconButton aria-label="To Paid" size="xs"
                                                        icon={<Icon as={AngleDoubleRight} boxSize="16px"/>}
                                                        onClick={actionMenu.onBulkToPaid}
                                                        isDisabled={actionMenu.nextStageDisabled}/>
                                        </Tooltip>
                                    )}
                                </HStack>
                            ) : (
                                actionMenu ? (
                                <HStack spacing={1} alignItems="center">
                                <Menu placement="bottom-end" gutter={8} strategy="fixed">
                                    <MenuButton
                                        as={Button}
                                        {...navbarCartLikeButtonProps}
                                        {...(highlightActionForSelection
                                            ? {
                                                bg: "blue.500",
                                                color: "white",
                                                borderWidth: "1px",
                                                borderColor: "blue.600",
                                                _hover: {
                                                    bg: "blue.600",
                                                    color: "white",
                                                    textDecoration: "none",
                                                },
                                                _active: {
                                                    bg: "blue.700",
                                                    color: "white",
                                                },
                                                _expanded: {
                                                    bg: "blue.500",
                                                    color: "white",
                                                },
                                                sx: {
                                                    "& .chakra-button__icon": {color: "white"},
                                                },
                                            }
                                            : {})}
                                        size="xs"
                                        h="auto"
                                        py={1}
                                        px={2}
                                        minH={0}
                                        rightIcon={<BsThreeDotsVertical size={13}/>}
                                    >
                                        Action
                                    </MenuButton>
                                    <MenuList fontSize="xs" sx={actionMenuListSx}>
                                        {typeof uiModeIsLegacy === "boolean" && onUiModeToggle && (
                                            <>
                                                <MenuGroup title="VIEW">
                                                    <MenuItem
                                                        icon={<Icon as={FiRepeat} fontSize="16px"/>}
                                                        onClick={() => onUiModeToggle(true)}
                                                    >
                                                        Switch UI
                                                    </MenuItem>
                                                </MenuGroup>
                                                <MenuDivider/>
                                            </>
                                        )}
                                        {actionMenu.hasExportSection && (
                                            <MenuGroup title="EXPORT">
                                                <MenuItem
                                                    icon={<Icon as={AiOutlineFileExcel} fontSize="18px"/>}
                                                    fontSize="xs"
                                                    onClick={actionMenu.onExportStatement}
                                                >
                                                    Export Statement
                                                </MenuItem>
                                                <MenuItem
                                                    icon={actionMenuFiIcon(FiShoppingBag)}
                                                    fontSize="xs"
                                                    onClick={actionMenu.onExportSales}
                                                >
                                                    Export of sales
                                                </MenuItem>
                                            </MenuGroup>
                                        )}
                                        {actionMenu.hasExportSection &&
                                            (actionMenu.hasBulkInvoiceSection || actionMenu.hasNextStageSection) && (
                                            <MenuDivider/>
                                        )}
                                        {actionMenu.hasBulkInvoiceSection && (
                                            <MenuGroup title="INVOICE">
                                                <MenuItem
                                                    icon={actionMenuFiIcon(FiDownload)}
                                                    fontSize="xs"
                                                    onClick={actionMenu.onDownloadInvoice}
                                                >
                                                    Download
                                                </MenuItem>
                                                {actionMenu.showInvoiceMenuItem && (
                                                    <MenuItem
                                                        icon={actionMenuFiIcon(FiEye)}
                                                        fontSize="xs"
                                                        onClick={actionMenu.onShowInvoice}
                                                    >
                                                        Show PDF
                                                    </MenuItem>
                                                )}
                                                {actionMenu.canPrintSelect && (
                                                    <MenuItem
                                                        icon={actionMenuFiIcon(FiPrinter)}
                                                        fontSize="xs"
                                                        onClick={actionMenu.onPrintSelect}
                                                    >
                                                        Print select
                                                    </MenuItem>
                                                )}
                                                {actionMenu.canSendSelect && (
                                                    <MenuItem
                                                        icon={actionMenuFiIcon(FiSend)}
                                                        fontSize="xs"
                                                        onClick={actionMenu.onSendSelect}
                                                        isDisabled={actionMenu.sendSelectDisabled}
                                                    >
                                                        Send select
                                                    </MenuItem>
                                                )}
                                            </MenuGroup>
                                        )}
                                        {actionMenu.hasNextStageSection && (
                                            <MenuGroup title="NEXT STAGE">
                                                <MenuItem
                                                    icon={<Icon as={AngleDoubleRight} boxSize="18px"/>}
                                                    fontSize="xs"
                                                    onClick={actionMenu.onBulkToPaid}
                                                    isDisabled={actionMenu.nextStageDisabled}
                                                >
                                                    To Paid
                                                </MenuItem>
                                            </MenuGroup>
                                        )}
                                    </MenuList>
                                </Menu>
                                {showCardPaidStatusFilter && onCardPaidFilterClick && (
                                    <CardPaidFilterShortcut
                                        count={cardPaidOrderCount ?? null}
                                        isActive={!!isCardPaidFilterActive}
                                        onClick={onCardPaidFilterClick}
                                    />
                                )}
                                </HStack>
                                ) : (
                                    <HStack spacing={1}>
                                        {showCardPaidStatusFilter && onCardPaidFilterClick && (
                                            <CardPaidFilterShortcut
                                                count={cardPaidOrderCount ?? null}
                                                isActive={!!isCardPaidFilterActive}
                                                onClick={onCardPaidFilterClick}
                                            />
                                        )}
                                        {typeof uiModeIsLegacy === "boolean" && onUiModeToggle && (
                                            <Tooltip label="Switch UI">
                                                <IconButton
                                                    aria-label="Switch UI"
                                                    size="xs"
                                                    icon={<Icon as={FiRepeat}/>}
                                                    onClick={() => onUiModeToggle(true)}
                                                />
                                            </Tooltip>
                                        )}
                                    </HStack>
                                )
                            )}
                            </Flex>
                        </Box>
                    )}
                </Flex>
            </Box>
        </Box>
    );
};
